<template>
  <el-dialog
    :visible="visible"
    title="方案修改"
    class="combo-dialog"
    @close="onClose"
    :fullscreen="true"
  >
    <el-tabs v-model="searchData.comboType">
      <el-tab-pane
        v-for="status in tabs"
        :label="status.name"
        :name="status.value.toString()"
        :key="status.value"
      />
    </el-tabs>
    <div class="dialog-save">
      <el-button
        class="screenshot-button"
        size="small"
        type="success"
        round
        :loading="capturing"
        :disabled="listRequestInFlight || list.length === 0"
        @click="captureCombos"
        >一键截图</el-button
      >
      <el-radio-group
        v-model="showRetailPrice"
        size="small"
        class="retail-toggle"
      >
        <el-radio-button :label="true">显示零售价</el-radio-button>
        <el-radio-button :label="false">隐藏零售价</el-radio-button>
      </el-radio-group>
      <el-button size="small" type="primary" :loading="saving" @click="onSubmit"
        >保 存</el-button
      >
    </div>
    <div class="combo-body">
      <el-empty
        v-if="list.length === 0 && !listRequestInFlight"
        class="combo-empty"
        description="暂无记录"
      />
      <div v-else-if="list.length > 0" class="prepay-rate-list">
        <div
          v-for="(item, index) in groupedList"
          :key="item.prepayRate === null ? 'unknown' : item.prepayRate"
          class="prepay-rate-group"
        >
          <div class="prepay-rate-name">
            <div>首付比</div>
            <div>
              {{ item.prepayRate === null ? "未设置" : `${item.prepayRate}%` }}
            </div>
          </div>
          <div class="combo-list">
            <div class="divider" v-if="index > 0" />
            <combo-card
              v-for="combo in item.list"
              :key="combo.comboId"
              :value="combo"
              :checked="form.comboId === combo.comboId"
              :showRetailPrice="showRetailPrice"
              :salePrice="salePrice"
              @checkedChange="onCheckedChange(combo.comboId)"
            />
          </div>
        </div>
      </div>
    </div>
    <div v-if="captureData" class="screenshot-stage" aria-hidden="true">
      <ScreenshotPoster ref="screenshotPoster" v-bind="captureData" />
    </div>
  </el-dialog>
</template>

<script>
import {
  EnumComboType,
  ComboTypeList,
  EnumComboRoute,
} from "@/enum/dict/index.js";

import ComboCard from "./combo-card.vue";
import { getStoreComboList, editStoreComboList } from "../api.js";
import { downloadAsFile } from "@/utils/export";
import ScreenshotPoster from "./screenshot-poster.vue";

export default {
  name: "EditCombo",
  components: { ComboCard, ScreenshotPoster },
  props: {
    customerName: { type: String, default: "" },
    customerMobile: { type: String, default: "" },
    visible: {
      type: Boolean,
      required: true,
    },
    orderNo: {
      type: String,
      required: false,
      default: null,
    },
    comboId: {
      type: Number,
      required: false,
      default: null,
    },
    comboRoute: {
      type: Number,
      required: false,
      default: EnumComboRoute.ROUTE_1,
    },
  },
  data() {
    return {
      searchData: {
        comboType: EnumComboType.A.toString(),
      },
      form: {
        remark: undefined,
        comboId: undefined,
      },
      list: [],
      comboDataMap: {},
      enableComboTypeList: [],
      // 存在不可选方案时默认显示零售价。
      showRetailPrice: false,
      // 接口返回的零售价。
      salePrice: "",
      // 加载期间不展示空状态。
      listRequestInFlight: false,
      saving: false,
      capturing: false,
      captureData: null,
      dict: {
        EnumComboType,
        ComboTypeList,
      },
    };
  },
  computed: {
    tabs() {
      return this.dict.ComboTypeList.filter((item) =>
        this.enableComboTypeList.includes(item.value.toString()),
      );
    },
    groupedList() {
      // 相同首付比归为一组，按数值升序展示，缺失值放在末尾。
      const groups = new Map();
      this.list.forEach((combo) => {
        const prepayRate = Number.isFinite(combo.prepayRate)
          ? combo.prepayRate
          : null;
        if (!groups.has(prepayRate)) {
          groups.set(prepayRate, { prepayRate, list: [] });
        }
        groups.get(prepayRate).list.push(combo);
      });
      let count = 0;
      return Array.from(groups.values())
        .sort((a, b) => {
          if (a.prepayRate === null) return 1;
          if (b.prepayRate === null) return -1;
          return a.prepayRate - b.prepayRate;
        })
        .map((group) => ({
          ...group,
          list: group.list.map((combo) => ({ ...combo, index: ++count })),
        }));
    },
  },
  watch: {
    visible(val) {
      // 打开时立即清空旧列表，避免闪现上次数据。
      if (val) {
        this.prepareOpen();
      }
    },
    "searchData.comboType"() {
      this.updateListByTab();
    },
  },
  methods: {
    async captureCombos() {
      if (this.capturing) return;
      this.capturing = true;
      this.captureData = {
        customerName: this.customerName,
        customerMobile: this.customerMobile,
        groups: this.groupedList,
        showRetailPrice: this.showRetailPrice,
        salePrice: this.salePrice,
      };
      const filename = `OK租机租赁方案-${this.orderNo}.png`;
      try {
        // PNG Promise 在点击事件内交给剪贴板，保留浏览器要求的用户手势。
        const imagePromise = this.$nextTick().then(() =>
          this.$refs.screenshotPoster.toBlob(),
        );
        if (
          navigator.clipboard &&
          navigator.clipboard.write &&
          window.ClipboardItem
        ) {
          await navigator.clipboard
            .write([new window.ClipboardItem({ "image/png": imagePromise })])
            .then(
              () => {
                this.$message.success("方案截图已复制，可直接粘贴");
              },
              async () => {
                downloadAsFile(await imagePromise, filename);
                this.$message.warning("无法写入剪贴板，已下载方案截图");
              },
            );
        } else {
          downloadAsFile(await imagePromise, filename);
          this.$message.warning("当前浏览器不支持复制图片，已下载方案截图");
        }
      } catch (error) {
        console.error("方案截图失败", error);
        this.$message.error("截图失败，请重试");
      } finally {
        this.captureData = null;
        this.capturing = false;
      }
    },
    onCheckedChange(id) {
      this.form.comboId = id;
    },
    prepareOpen() {
      this.listRequestInFlight = true;
      this.searchData = { comboType: EnumComboType.A.toString() };
      this.form = { remark: undefined, comboId: this.comboId };
      this.list = [];
      this.comboDataMap = {};
      this.enableComboTypeList = [];
      this.salePrice = "";

      this.searchData.comboType =
        this.comboRoute == EnumComboRoute.ROUTE_2
          ? EnumComboType.B.toString()
          : EnumComboType.A.toString();

      this.getList();
    },
    updateListByTab() {
      this.list = this.comboDataMap[this.searchData.comboType] || [];
      // 零售价默认展示规则（按当前 tab）：全部方案可选时默认隐藏；存在不可选方案时默认显示
      this.showRetailPrice = this.list.some(
        (item) => item.selectable === false,
      );
    },
    async getList() {
      this.listRequestInFlight = true;
      try {
        const res = await getStoreComboList({
          orderNo: this.orderNo,
          comboRoute:
            this.searchData.comboType === this.dict.EnumComboType.A.toString()
              ? this.searchData.comboRoute
              : undefined,
        });

        const enableComboTypeList = (res.enableComboTypeList || []).map(
          (item) => item.toString(),
        );
        this.enableComboTypeList = enableComboTypeList;

        this.comboDataMap = {
          [this.dict.EnumComboType.A.toString()]: res.normalComboList || [],
          [this.dict.EnumComboType.B.toString()]: res.rewardComboList || [],
        };
        this.salePrice = res.salePrice;

        if (!enableComboTypeList.includes(this.searchData.comboType)) {
          this.searchData.comboType =
            enableComboTypeList[0] || this.dict.EnumComboType.A.toString();
        }

        this.updateListByTab();

        const existComboId = this.list.find(
          (item) => item.comboId === this.comboId,
        );
        this.form.comboId = existComboId ? this.comboId : null;
      } finally {
        this.listRequestInFlight = false;
      }
    },
    onClose() {
      this.listRequestInFlight = false;
      this.$emit("update:visible", false);
    },
    async onSubmit() {
      if (this.saving) return;
      const combo = this.list.find(
        (item) => item.comboId === this.form.comboId,
      );

      if (!combo) {
        this.$message.error("请选择方案");

        return;
      }

      // 确认和请求期间锁定，取消或失败后允许重试。
      this.saving = true;
      try {
        await this.$confirm(
          `是否确认 <strong>${combo.comboName}</strong> 为新方案？`,
          {
            dangerouslyUseHTMLString: true,
          },
        );

        await editStoreComboList({
          orderNo: this.orderNo,
          comboTab: this.searchData.comboType,
          comboId: this.form.comboId,
          remark:
            this.searchData.comboType == EnumComboType.B
              ? this.form.remark
              : null,
        });

        this.$message({
          type: "success",
          message: "操作成功",
        });

        this.onClose();

        localStorage.setItem("is_update_period", "true");

        this.$emit("refresh");
      } catch (error) {
        // 取消无需提示，接口错误由 http 统一提示。
      } finally {
        this.saving = false;
      }
    },
  },
};
</script>
<style scoped lang="scss">
.combo-dialog {
  /deep/ {
    .el-dialog {
      margin-bottom: 0px !important;
    }
  }
}

.combo-body {
  display: flex;
  flex-direction: column;
  max-height: calc(100vh - 5vh - 140px);

  .combo-empty {
    padding: 48px 0;
    flex: 1;
  }

  .prepay-rate-list {
    display: flex;
    flex-direction: column;
    overflow-y: auto;
    flex: 1;
    max-height: 100%;
  }
}

.prepay-rate-group {
  display: flex;
  align-items: center;
  gap: 10px;

  .prepay-rate-name {
    text-align: center;
    font-size: 16px;
    font-weight: bold;
  }

  .combo-list {
    position: relative;
    flex: 1;
    min-width: 0;
    display: flex;
    flex-wrap: wrap;
    row-gap: 30px;
    column-gap: 10px;
    padding: 30px 10px;
    background-color: #ddd;

    .divider {
      position: absolute;
      top: -15px;
      left: 10px;
      right: 10px;
      border-top: 1px dashed #000;
    }
  }
}

.dialog-save {
  position: absolute;
  right: 20px;
  top: 65px;
  display: flex;
  align-items: center;
  gap: 12px;
}
.screenshot-button {
  background-color: #12c99b;
  border-color: #12c99b;
  padding: 9px 24px;
  font-weight: 600;
  &:hover,
  &:focus {
    background-color: #28d4aa;
    border-color: #28d4aa;
  }
}
.screenshot-stage {
  position: fixed;
  top: 0;
  left: -10000px;
  pointer-events: none;
}

.retail-toggle {
  /deep/ {
    display: inline-flex;
    align-items: center;
    padding: 3px;
    background-color: #f0f2f5;
    border-radius: 999px;

    .el-radio-button__inner {
      padding: 0 16px;
      line-height: 26px;
      border: none;
      border-radius: 999px;
      background-color: transparent;
      color: #909399;
      font-weight: 500;
      box-shadow: none !important;
      transition: all 0.25s ease;

      &:hover {
        color: #409eff;
      }
    }

    .el-radio-button__orig-radio:checked + .el-radio-button__inner {
      background: linear-gradient(135deg, #409eff 0%, #2f8df5 100%);
      color: #fff;
      font-weight: 600;
      box-shadow: 0 2px 6px rgba(64, 158, 255, 0.35);
    }
  }
}
</style>
